/** The live shell owns screen identity; guests only supply bounded page facts. */
import { useEffect, useRef, useSyncExternalStore } from "react";
import { api, setChatScreenReporter, type ScreenPage, type ScreenReport } from "./api";
import { withDeadline } from "./deadline";

export interface ScreenView extends ScreenPage {
  title: string;
  viewContext: Record<string, string>;
  content?: unknown;
}
interface PageDisplay { title: string; context: Record<string, string> }
export interface ScreenTarget { panel_key: string; context: Record<string, string> }
const held = new Map<string, { count: number; page?: ScreenPage; flush?: () => Promise<boolean>; display?: PageDisplay; content?: unknown }>();
const listeners = new Set<() => void>();
const identityReady = new Set<() => void>();
let views: ScreenView[] = [];
let flushIdentity: (() => Promise<boolean>) | undefined;
function changed(): void {
  views = [...held.values()].flatMap(value => value.page && value.display
    ? [{ ...value.page, title: value.display.title, viewContext: value.display.context, content: value.content }] : []);
  listeners.forEach(listener => listener());
}
/** The exact live identities used by chat, with local display metadata only. */
export function useScreenViews(): ScreenView[] {
  return useSyncExternalStore(listener => { listeners.add(listener); return () => listeners.delete(listener); }, () => views);
}

/** A receipt can describe only a showing still held by this document. */
export function reportScreenContent(showing: string, content: unknown): void {
  const value = held.get(showing);
  if (!value || value.content === content) return;
  value.content = content;
  changed();
}
/** Recovery is an edge of the actual identity write, never a timer or guest retry. */
export function onScreenIdentityReady(listener: () => void): () => void {
  identityReady.add(listener);
  return () => identityReady.delete(listener);
}
export function displayScreenPage(showing: string, display: PageDisplay): void {
  const value = held.get(showing);
  if (!value || JSON.stringify(value.display) === JSON.stringify(display)) return;
  const coordinates = (context: Record<string, string> = {}) => JSON.stringify(Object.entries(context).sort(([a], [b]) => a.localeCompare(b)));
  const selectionChanged = coordinates(value.display?.context) !== coordinates(display.context);
  value.display = display;
  // Selection changes retire the old object's name before the next receipt.
  if (selectionChanged) value.content = undefined;
  changed();
}

/** Hold a live page even before its guest has initialized or reported. */
export function holdOnScreen(showing: string, page?: Omit<ScreenPage, "showing">, flush?: () => Promise<boolean>, display?: PageDisplay): () => void {
  const previous = held.get(showing);
  held.set(showing, { ...previous, count: (previous?.count ?? 0) + 1, page: page ? { showing, ...page } : previous?.page,
    flush: flush ?? previous?.flush, display: display ?? previous?.display });
  changed();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const value = held.get(showing);
    if (value && value.count > 1) { value.count--; return; }
    held.delete(showing); changed();
  };
}
export function onScreen(): string[] { return [...held.keys()].sort(); }
function matches(page: ScreenPage | undefined, target: ScreenTarget | null | undefined): boolean {
  if (target === undefined) return true;
  if (target === null || page?.panel_key !== target.panel_key) return false;
  const fields = (context: Record<string, string>) => JSON.stringify(Object.entries(context).sort(([a], [b]) => a.localeCompare(b)));
  return fields(page.context) === fields(target.context);
}
/** Host CAS writes wait until their identity claim has been attempted. */
export async function flushScreenIdentity(): Promise<boolean> { return await flushIdentity?.() ?? true; }

/** One in-flight claim, coalesced changes, and a monotonic leave watermark. */
export function useScreenReport(looking: boolean, sessionId?: string, target?: ScreenTarget | null): ScreenView[] {
  const currentViews = useScreenViews();
  const lookingNow = useRef(looking), sessionNow = useRef(sessionId);
  const targetNow = useRef(target);
  targetNow.current = target;
  lookingNow.current = looking; sessionNow.current = sessionId;
  const report = useRef<() => void>(() => undefined);
  useEffect(() => {
    const tab = crypto.randomUUID();
    let revision = 0, sent: string | null = null, away = false, again = false, accepted = true, identityAvailable = false;
    let inflight: Promise<void> | null = null;
    const selected = () => [...held.entries()].filter(([, value]) => matches(value.page, targetNow.current));
    const snapshot = (targetSession = sessionNow.current): Omit<ScreenReport, "revision"> => ({
      tab, showing: lookingNow.current && !away ? selected().map(([showing]) => showing).sort() : [],
      pages: lookingNow.current && !away ? selected().flatMap(([, value]) => value.page ? [value.page] : []) : [],
      ...(targetSession ? { session_id: targetSession } : {}),
      ...(!lookingNow.current || away ? { left: true } : {}),
    });
    const signature = () => lookingNow.current && !away ? JSON.stringify(snapshot()) : null;
    const send = async (body: Omit<ScreenReport, "revision">) => { await api.screen({ ...body, revision: ++revision }); };
    const run = async (force = false): Promise<void> => {
      if (inflight) { again = true; await inflight; return; }
      if (!force && signature() === sent) return;
      inflight = (async () => {
        do {
          again = false;
          const want = signature();
          if (!force && want === sent) break;
          force = false;
          try {
            await send(snapshot()); accepted = true;
            if (!away) sent = want;
            if (!identityAvailable && want === signature() && lookingNow.current && !away) {
              identityAvailable = true;
              identityReady.forEach(listener => listener());
            }
          }
          catch { accepted = false; identityAvailable = false; /* No automatic retry; chat carries identity too. */ }
        } while (again);
      })();
      try { await inflight; } finally { inflight = null; }
    };
    const identity = async () => {
      const available = await withDeadline(run().then(() => true), false) && accepted;
      if (!available) identityAvailable = false;
      return available;
    };
    flushIdentity = identity;
    const stopChat = setChatScreenReporter(async targetSession => {
      // Claim first so the guest's final summary belongs to this live showing.
      const identityAccepted = await withDeadline(run(true).then(() => true), false) && accepted;
      const prepared = signature();
      const pages = selected();
      const results = identityAccepted ? await Promise.all(pages.map(async ([showing, value]) => {
        const available = value.flush ? await Promise.resolve().then(value.flush).catch(() => false) : true;
        if (!available) reportScreenContent(showing, undefined);
        return available;
      })) : [false];
      const finalIdentityAccepted = identityAccepted && await withDeadline(run().then(() => true), false) && accepted;
      if (!finalIdentityAccepted) selected().forEach(([showing]) => reportScreenContent(showing, undefined));
      // A failed flush explicitly retires old content under the same showing.
      // Only identity rides on chat, never a page snapshot.
      return { ...snapshot(targetSession), revision: ++revision,
        ...(!identityAccepted || !finalIdentityAccepted || signature() !== prepared || results.includes(false) ? { content_unavailable: true } : {}) };
    });
    const listener = () => { void run(); };
    report.current = listener; listeners.add(listener);
    const leave = () => {
      if (away) return;
      away = true;
      if (sent === null && !inflight) return;
      sent = null;
      // Never wait behind a claim: its lower revision cannot resurrect us.
      void send(snapshot()).catch(() => undefined);
    };
    const back = (event: PageTransitionEvent) => { if (event.persisted) { away = false; void run(); } };
    window.addEventListener("pagehide", leave); window.addEventListener("pageshow", back);
    void run();
    return () => {
      listeners.delete(listener); stopChat();
      if (flushIdentity === identity) flushIdentity = undefined;
      window.removeEventListener("pagehide", leave); window.removeEventListener("pageshow", back);
      report.current = () => undefined; leave();
    };
  }, []);
  const targetKey = JSON.stringify(target);
  useEffect(() => { report.current(); }, [looking, sessionId, targetKey]);
  return looking ? currentViews.filter(view => matches(view, target)) : [];
}
